<script lang="ts">
  /**
   * The button, ported from the company site's `shared/ui/Button`.
   *
   * What came across are its decisions: one radius for every control, fixed heights per size rather
   * than whatever padding produces, weight 700, and a 200ms ease on every state change. What did not
   * is its disabled state — the site sets the background and the text colour to the same variable, so
   * a disabled button renders as a solid block with invisible text. Ours dims instead.
   *
   * The site has three variants and all of them are filled; this UI needs a fourth constantly — a
   * bordered secondary for "load only", "clear filters", "cancel" — so `secondary` and `ghost` are
   * additions rather than ports, built from the same tokens so they sit in the same system.
   */
  import type { Snippet } from "svelte";

  let {
    variant = "primary",
    size = "md",
    disabled = false,
    block = false,
    type = "button",
    onclick,
    children,
    ...rest
  }: {
    variant?: "primary" | "secondary" | "ghost" | "danger";
    size?: "lg" | "md" | "sm" | "xs";
    disabled?: boolean;
    /** Stretches to the container. For dialog footers and anywhere a button is the whole row. */
    block?: boolean;
    type?: "button" | "submit";
    onclick?: (event: MouseEvent) => void;
    children?: Snippet;
    [key: string]: unknown;
  } = $props();

  const VARIANTS = {
    primary: "bg-accent text-white border-accent hover:brightness-95 active:brightness-90",
    secondary: "bg-white text-ink border-line hover:border-accent hover:text-accent",
    ghost: "bg-transparent text-ink-muted border-transparent hover:text-accent hover:bg-panel",
    danger: "bg-transparent text-error border-error hover:bg-error hover:text-white",
  } as const;

  // Height comes from the token; padding only decides how much room the label gets beside it.
  const SIZES = {
    lg: "h-[var(--brand-control-h-lg)] px-8 text-base",
    md: "h-[var(--brand-control-h-md)] px-4 text-sm",
    sm: "h-[var(--brand-control-h-sm)] px-3 text-xs",
    xs: "h-[var(--brand-control-h-xs)] px-2.5 text-xs",
  } as const;
</script>

<button
  {type}
  {disabled}
  {onclick}
  {...rest}
  class="inline-flex items-center justify-center gap-2 rounded-control border font-bold whitespace-nowrap
         transition-[background-color,color,border-color,filter] duration-200 ease-out
         disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100
         {VARIANTS[variant]} {SIZES[size]} {block ? 'w-full' : ''}"
>
  {@render children?.()}
</button>
